import { useState } from 'react';

import { ArrowBack, Close, ContentCopy } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import {
  Button,
  IconButton,
  Snackbar,
  SnackbarContent,
  Typography,
} from '@mui/material';
import { wellzTheme } from '@wellz-theme/mui';
import {
  informationInviteAtom,
  infoSessionCancelledAtom,
} from 'atoms/inviteSession';
import { useAtomValue } from 'jotai';
import { FormattedMessage, useIntl } from 'react-intl';
import { wellzTheme as theme } from 'theme';
import { formatDate } from 'utils/helpers';

import DailyActivities from './daily-activities.svg';
import * as S from './style';

import { DrawerInviteCreatedProps } from './types';

const DrawerInviteCreatedUnmanaged: React.FC<
  DrawerInviteCreatedProps & { subtitle: string; link: string }
> = ({ onCloseButton, onConfirm, onSeeInvites, onGoBack, subtitle, link }) => {
  const intl = useIntl();
  const [hasSnackbar, setHasSnackbar] = useState(false);

  return (
    <>
      <S.Container>
        <S.Header>
          {onGoBack ? (
            <S.SimpleButton
              variant="text"
              style={{ padding: wellzTheme.spacing(1.5) }}
              data-testid="goBackButton"
              onClick={onGoBack}
            >
              <ArrowBack
                style={{ fill: theme.colors.primary }}
                width={wellzTheme.spacing(3)}
                height={wellzTheme.spacing(3)}
              />
            </S.SimpleButton>
          ) : (
            <div style={{ width: wellzTheme.spacing(6) }} />
          )}

          <S.SectionTitle>
            <FormattedMessage id="CPPLATFORM.CPINVITECREATING.HEADER" />
          </S.SectionTitle>

          <S.SimpleButton
            variant="text"
            style={{ padding: wellzTheme.spacing(1.5) }}
            onClick={onCloseButton}
            data-testid="closeButton"
          >
            <Close
              style={{ fill: theme.colors.primary }}
              width={wellzTheme.spacing(3)}
              height={wellzTheme.spacing(3)}
            />
          </S.SimpleButton>
        </S.Header>

        <S.Body>
          <DailyActivities />
          <div style={{ height: wellzTheme.spacing(2) }} />
          <Typography variant="h5">
            <FormattedMessage id="CPPLATFORM.CPINVITESUCCESSFULLY.TITLE" />
          </Typography>
          <div style={{ height: wellzTheme.spacing(1) }} />
          <Typography
            variant="body1"
            style={{ color: theme.colors.text.disabled }}
          >
            {subtitle}
          </Typography>
          <div style={{ height: wellzTheme.spacing(2) }} />
          <S.LinkAndButton>
            <S.LinkBox>
              <S.LinkText variant="body1">{link}</S.LinkText>
            </S.LinkBox>

            <Button
              variant="contained"
              size="large"
              onClick={() => {
                navigator.clipboard.writeText(link);
                setHasSnackbar(true);
              }}
            >
              <ContentCopy style={{ fill: 'white' }} />
              <div style={{ width: wellzTheme.spacing(1) }} />
              <FormattedMessage id="CPPLATFORM.CPINVITESUCCESSFULLY.COPYLINKBUTTON" />
            </Button>
          </S.LinkAndButton>
          <div style={{ height: wellzTheme.spacing(2) }} />
          <Typography
            variant="body2"
            style={{ color: theme.colors.text.disabled }}
          >
            <FormattedMessage id="CPPLATFORM.CPINVITESUCCESSFULLY.SUBTITLE2" />
          </Typography>
        </S.Body>
        <S.Footer>
          <Button variant="contained" size="large" onClick={onConfirm}>
            <FormattedMessage id="CPPLATFORM.CPINVITESUCCESSFULLY.CONFIRMBUTTON" />
          </Button>
          <Button size="large" variant="text" onClick={onSeeInvites}>
            <FormattedMessage id="CPPLATFORM.CPINVITESUCCESSFULLY.SEEINVITESBUTTON" />
          </Button>
        </S.Footer>
      </S.Container>

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={hasSnackbar}
        autoHideDuration={3000}
        onClose={() => setHasSnackbar(false)}
      >
        <SnackbarContent
          message={intl.formatMessage({
            id: 'CPPLATFORM.CPINVITECONFIRM.LINKCOPIED',
          })}
          sx={{ backgroundColor: theme.colors.grade.low }}
          action={(() => (
            <IconButton
              aria-label="close"
              sx={{ p: 0.5 }}
              color="inherit"
              onClick={() => {
                setHasSnackbar(false);
              }}
            >
              <CloseIcon />
            </IconButton>
          ))()}
        />
      </Snackbar>
    </>
  );
};

export const DrawerInviteCreated: React.FC<DrawerInviteCreatedProps> =
  props => {
    const intl = useIntl();
    const informationInvite = useAtomValue(informationInviteAtom);
    const infoSessionCancelled = useAtomValue(infoSessionCancelledAtom);
    const patientName = infoSessionCancelled
      ? infoSessionCancelled.fullName
      : informationInvite.patientName;
    const sessionCreated = formatDate(informationInvite.sessionCreated, {
      locale: intl.locale,
    });

    return (
      <DrawerInviteCreatedUnmanaged
        {...props}
        subtitle={intl.formatMessage(
          {
            id: infoSessionCancelled
              ? 'CPPLATAFORM.CPNEWTIMECONFIRM.SUCCESSFULLYSUBTITLE'
              : 'CPPLATFORM.CPINVITESUCCESSFULLY.SUBTITLE',
          },
          {
            SessionCreated: sessionCreated,
            PatientName: patientName,
          },
        )}
        link={informationInvite.inviteLink}
      />
    );
  };
